<template>
    <cod-dialog
        ref="dialog"
        :show.sync="showDialog"
        :drawer="drawer"
        :loading="busy"
        max-width="720px"
        color="primary"
    >
        <template>
            <!-- Header -->
            <div class="header" slot="header">
                {{ $t('general.sdc_short') }} - {{ $t('general.sdc') }}
            </div>

            <!-- Content -->
            <div class="sdc-mgmt-content" slot="content">
                <div v-if="loaded">
                    <v-tabs v-model="seElType" class="sdc-tabs" grow>
                        <v-tab>
                            <v-icon class="mr-3" :color="seElType === 0 ? 'primary' : '#222222'">
                                fal fa-sim-card
                            </v-icon>
                            {{ $t('sdc.security_element_card') }}
                        </v-tab>
                        <v-tab>
                            <v-icon class="mr-3" :color="seElType === 1 ? 'primary' : '#222222'">
                                fal fa-file-certificate
                            </v-icon>
                            {{ $t('sdc.security_element_file') }}
                        </v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="seElType">
                        <v-tab-item class="sdc-tab-content">
                            <div
                                v-if="sdcStatus.data || uiData"
                                class="sdc-info-container"
                            >
                                <div cols="12" sm="6" class="sdc-info pa-4">
                                    <div v-if="sdcStatus.data">
                                        <div style="uppercase">
                                            <strong>{{ sdcStatus.data.model }} - v{{ sdcStatus.data.softwareVersion }} ({{ sdcStatus.data.make }})</strong>
                                        </div>
                                        <div>{{ $t('sdc.esdc.serial') }}: <strong>{{ sdcStatus.data.deviceSerialNumber }}</strong></div>
                                    </div>

                                    <v-divider v-if="uiData && sdcStatus.data" class="my-2 mb-3" />

                                    <div v-if="uiData">
                                        <div v-if="!!uiData.hasCard" class="d-flex mb-1">
                                            <v-icon class="mr-2" size="20" color="success">fas fa-check-circle</v-icon>
                                            <div>{{ $t('sdc.esdc.card_present') }}</div>
                                        </div>
                                        <div v-else class="d-flex mb-1">
                                            <v-icon class="mr-2" size="20" color="error">fas fa-exclamation-circle</v-icon>
                                            <div>{{ $t('sdc.esdc.card_present_not') }}</div>
                                        </div>
                                        <div v-if="!!uiData.isCardUnlocked" class="d-flex mb-1">
                                            <v-icon class="mr-2" size="20" color="success">fas fa-check-circle</v-icon>
                                            <div>{{ $t('sdc.esdc.card_unlocked') }}</div>
                                        </div>
                                        <div v-else class="d-flex mb-1">
                                            <v-icon class="mr-2" size="20" color="error">fas fa-exclamation-circle</v-icon>
                                            <div>{{ $t('sdc.esdc.card_unlocked_not') }}</div>
                                        </div>
                                        <div v-if="!!uiData.hasInternetConnection" class="d-flex mb-1">
                                            <v-icon class="mr-2" size="20" color="success">fas fa-check-circle</v-icon>
                                            <div>{{ $t('sdc.esdc.internet_active') }}</div>
                                        </div>
                                        <div v-else class="d-flex mb-1">
                                            <v-icon class="mr-2" size="20" color="error">fas fa-exclamation-circle</v-icon>
                                            <div>{{ $t('sdc.esdc.internet_active_not') }}</div>
                                        </div>
                                        <div v-if="!uiData.isAuditRequired" class="d-flex mb-1">
                                            <v-icon class="mr-2" size="20" color="success">fas fa-check-circle</v-icon>
                                            <div>{{ $t('sdc.esdc.audit_required_not') }}</div>
                                        </div>
                                        <div v-else class="d-flex mb-1">
                                            <v-icon class="mr-2" size="20" color="error">fas fa-exclamation-circle</v-icon>
                                            <div>{{ $t('sdc.esdc.audit_required') }}</div>
                                        </div>
                                        <div v-if="uiData.certExpDate" class="d-flex mb-1">
                                            <v-icon v-if="moment(uiData.certExpDate).diff(moment(), 'days') >= 60" class="mr-2" size="20" color="success">fas fa-check-circle</v-icon>
                                            <v-icon v-else-if="moment(uiData.certExpDate).diff(moment(), 'days') <= 0" class="mr-2" size="20" color="error">fas fa-exclamation-circle</v-icon>
                                            <v-icon v-else class="mr-2" size="20" color="warning">fas fa-exclamation-circle</v-icon>
                                            <div>{{ $t('sdc.esdc.certificate_expires', { value: moment(uiData.certExpDate).format('DD.MM.YYYY.') }) }}</div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="uiData && uiData.taxpayer" cols="12" sm="6" class="sdc-info pa-4">
                                    <div class="d-flex align-center mb-1">
                                        <strong>{{ $t('sdc.esdc.data').toUpperCase() }}</strong>
                                    </div>
                                    <v-divider class="my-2 mb-3" />
                                    <div v-if="uiData.taxpayer.business_name" class="mb-1">
                                        {{ $t('sdc.esdc.company_name') }}: <strong>{{ uiData.taxpayer.business_name }}</strong>
                                    </div>
                                    <div v-if="uiData.taxpayer.tin" class="mb-1">
                                        {{ $t('sdc.esdc.tin') }}: <strong>{{ uiData.taxpayer.tin }}</strong>
                                    </div>
                                    <div v-if="uiData.taxpayer.shop_name" class="mb-1">
                                        {{ $t('sdc.esdc.business_unit') }}: <strong>{{ uiData.taxpayer.shop_name }}</strong>
                                    </div>
                                    <div v-if="uiData.taxpayer.city" class="mb-1">
                                        {{ $t('sdc.esdc.city') }}: <strong>{{ uiData.taxpayer.city }}</strong>
                                    </div>
                                    <div v-if="uiData.taxpayer.address_line_2 || uiData.taxpayer.municipality" class="mb-1">
                                        {{ $t('sdc.esdc.municipality') }}: <strong>{{ uiData.taxpayer.address_line_2 || uiData.taxpayer.municipality }}</strong>
                                    </div>
                                    <div v-if="uiData.taxpayer.address_line_1" class="mb-1">
                                        {{ $t('sdc.esdc.address') }}: <strong>{{ uiData.taxpayer.address_line_1 }}</strong>
                                    </div>
                                </div>
                            </div>

                            <div v-if="enableDevEsdc" class="my-5">
                                <v-divider />
                                <v-switch
                                    v-model="useDevEsdc"
                                    class="mt-4 mb-5"
                                    hide-details
                                    :label="$t('sdc.esdc.use_dev')"
                                    inset
                                />
                                <v-divider />
                            </div>

                            <div
                                v-if="device.platform !== 'android'"
                                class="d-flex justify-center align-center my-3"
                            >
                                <v-text-field
                                    v-model="esdcUrl"
                                    class="mb-4"
                                    :label="$t('sdc.esdc.url')"
                                    :placeholder="$t('sdc.esdc.url')"
                                    :disabled="useDevEsdc || busy"
                                    hide-details
                                />
                                <v-btn
                                    v-if="!useDevEsdc"
                                    class="ml-3"
                                    text
                                    :loading="busy"
                                    :disabled="!esdcUrl || esdcUrl.length <= 7 || useDevEsdc || !enableEsdcUrlUpdate"
                                    @click="updateEsdcUrl"
                                >
                                    {{ $t('general.save') }}
                                </v-btn>
                            </div>

                            <div v-if="!sdcStatus">
                                <v-alert
                                    type="warning"
                                    prominent
                                    text
                                >
                                    {{ $t('sdc.status.smart_card_not_present') }}
                                </v-alert>

                                <div class="d-flex justify-center">
                                    <v-btn
                                        class="mt-2"
                                        color="primary"
                                        :loading="busy"
                                        @click="checkEsdcStatus"
                                    >
                                        {{ $t('sdc.security_element_card_read') }}
                                    </v-btn>
                                </div>
                            </div>

                            <div v-else>
                                <div v-if="!busy && sdcStatus.error && sdcStatus.error.code === 'smart_card_not_present'">
                                    <v-alert
                                        type="error"
                                        prominent
                                        text
                                    >
                                        {{ $t('sdc.status.smart_card_not_present') }}
                                    </v-alert>

                                    <div class="d-flex justify-center">
                                        <v-btn
                                            class="mt-2"
                                            color="primary"
                                            :loading="busy"
                                            @click="checkEsdcStatus"
                                        >
                                            {{ $t('sdc.security_element_card_read') }}
                                        </v-btn>
                                    </div>
                                </div>

                                <div v-else>
                                    <div v-if="!busy && sdcStatus.error && sdcStatus.error.code === 'card_locked'">
                                        <v-alert
                                            type="error"
                                            prominent
                                            text
                                        >
                                            {{ $t('sdc.status.card_locked') }}
                                        </v-alert>
                                    </div>

                                    <div v-else-if="!busy && sdcStatus.error && sdcStatus.error.code === 'pin_required'">
                                        <v-alert
                                            v-if="!pinFailed"
                                            type="info"
                                            prominent
                                            text
                                        >
                                            {{ $t('sdc.status.pin_required') }}
                                        </v-alert>

                                        <v-alert
                                            v-else
                                            type="error"
                                            prominent
                                            text
                                        >
                                            {{ $t('sdc.status.pin_invalid') }}
                                        </v-alert>

                                        <v-text-field
                                            v-model="cardForm.pin"
                                            v-mask="'####'"
                                            inputmode="numeric"
                                            :label="$t('general.pin')"
                                            :placeholder="$t('general.pin')"
                                            :type="showPasswords ? 'text' : 'password'"
                                            :append-icon="showPasswords ? 'fal fa-eye' : 'fal fa-eye-slash'"
                                            :error-messages="cardForm.$errors.pin"
                                            @input="cardForm.$errors.pin = null"
                                            @click:append="showPasswords = !showPasswords"
                                        />

                                        <div class="d-flex justify-center">
                                            <v-btn
                                                class="mt-2"
                                                color="primary"
                                                :loading="busy"
                                                @click="verifyPin"
                                            >
                                                {{ $t('sdc.security_element_card_verify_pin') }}
                                            </v-btn>
                                        </div>
                                    </div>

                                    <div v-else-if="!busy && sdcStatus.error">
                                        <v-alert
                                            type="error"
                                            prominent
                                            text
                                        >
                                            {{ sdcStatus.error.message }}
                                        </v-alert>
                                    </div>

                                    <div v-else-if="!busy && !useDevEsdc && uiData && uiData.certExpDate && moment(uiData.certExpDate).diff(moment(), 'days') < 30">
                                        <v-alert
                                            type="error"
                                            prominent
                                            text
                                        >
                                            {{ $t('sdc.esdc.certificate_expires', { value: moment(uiData.certExpDate).format('DD.MM.YYYY.') }) }}
                                        </v-alert>
                                    </div>

                                    <v-alert
                                        v-else-if="!busy"
                                        type="success"
                                        prominent
                                        text
                                    >
                                        {{ $t('sdc.security_element_card_success') }}
                                    </v-alert>

                                </div>
                            </div>
                        </v-tab-item>

                        <v-tab-item class="sdc-tab-content">
                            <div v-if="!isCertificateSetup">
                                <v-slide-y-transition>
                                    <v-alert
                                        v-if="certError"
                                        type="error"
                                        text
                                    >
                                        {{ certError }}
                                    </v-alert>
                                </v-slide-y-transition>

                                <v-text-field
                                    v-model="certForm.pac"
                                    :type="showPasswords ? 'text' : 'password'"
                                    :label="$t('general.pac_placeholder')"
                                    :placeholder="$t('general.pac_placeholder')"
                                    :append-icon="showPasswords ? 'fal fa-eye' : 'fal fa-eye-slash'"
                                    :error-messages="certForm.$errors.pac"
                                    @input="certError = null"
                                    @click:append="showPasswords = !showPasswords"
                                />

                                <v-text-field
                                    v-if="device.platform === 'electron'"
                                    v-model="certForm.password"
                                    autocomplete="new-password"
                                    :type="showPasswords ? 'text' : 'password'"
                                    :label="$t('general.certificate_password')"
                                    :placeholder="$t('general.certificate_password')"
                                    :append-icon="showPasswords ? 'fal fa-eye' : 'fal fa-eye-slash'"
                                    :error-messages="certForm.$errors.password"
                                    @input="certError = null"
                                    @click:append="showPasswords = !showPasswords"
                                />

                                <div class="d-flex justify-center">
                                    <v-btn
                                        class="mt-4"
                                        color="primary"
                                        :loading="busy"
                                        @click="chooseCertificate"
                                    >
                                        {{ $t('sdc.security_element_file_select') }}
                                    </v-btn>
                                </div>
                            </div>

                            <v-alert
                                v-else
                                prominent
                                type="success"
                                text
                            >
                                <div>
                                    <div>{{ $t('sdc.security_element_file_success') }}</div>

                                    <div class="mt-4 d-flex">
                                        <v-btn
                                            color="success"
                                            outlined
                                            @click="isCertificateSetup = false"
                                        >
                                            {{ $t('sdc.security_element_file_change') }}
                                        </v-btn>

                                        <v-btn
                                            color="error"
                                            class="ml-3"
                                            @click="deleteCertificate"
                                        >
                                            {{ $t('sdc.security_element_file_delete') }}
                                        </v-btn>
                                    </div>
                                </div>
                            </v-alert>
                        </v-tab-item>
                    </v-tabs-items>
                </div>
            </div>
        </template>

    </cod-dialog>
</template>

<script>
import CodDialog from '@/components/CodDialog';
import FiscalService from '@/services/FiscalService';
import * as rules from 'vuelidate/lib/validators'

export default {
    components: {
        CodDialog,
    },

    props: {
        show: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            showDialog: false,
            drawer: null,
            busy: false,
            loaded: false,

            seElType: 0,
            cardForm: this.createCardForm(),
            certForm: this.createCertForm(),
            showPasswords: false,
            isCertificateSetup: false,
            certError: null,

            sdcStatus: null,
            uiData: null,
            pinFailed: false,
            useDevEsdc: false,
            customEsdcUrl: false,
            esdcUrl: null,

            enableEsdcUrlUpdate: true,
            enableDevEsdc: process.env.VUE_APP_ENV !== 'production'
        }
    },

    validations: {
        cardForm: {
            pin: { required: rules.required },
        },
        certForm: {
            password: { requiredIf: rules.requiredIf(function(form) {
                return this.device.platform === 'electron'
            }) },
            pac: { required: rules.required },
        }
    },

    watch: {
        async show(val) {
            this.showDialog = val;
            this.drawer = null;
            this.showPasswords = false;

            if(val) {
                this.loaded = false
                this.busy = true
                this.pinFailed = false
                this.useDevEsdc = FiscalService.useDevEsdc
                this.esdcUrl = FiscalService.getEsdcUrl()

                const certificate = await AppStorage.native().getItem('cert')
                const certificateAlias = await AppStorage.native().getItem('certAlias')
                this.isCertificateSetup = !!certificate || !!certificateAlias

                this.sdcStatus = null
                this.uiData = null
                this.seElType = 0
                this.cardForm = this.createCardForm()
                this.certForm = this.createCertForm()

                await this.checkSdcStatus()

                this.busy = false
                this.loaded = true
            } else {
                this.sdcStatus = null
                this.isCertificateSetup = false
                this.loaded = false
            }

            setTimeout(() => { this.drawer = null }, val ? 0 : 1500);
        },

        showDialog(val) {
            this.$emit('update:show', val);
        },

        async seElType(val) {
            if(val === 0) {
                this.busy = true
                await this.checkSdcStatus()
                this.busy = false
            }
        },

        async useDevEsdc(val) {
            this.busy = true
            FiscalService.useDevEsdc = val
            this.esdcUrl = val ? process.env.VUE_APP_ESDC_DEV_URL : FiscalService.getEsdcUrl()

            if(val) {
                await this.checkSdcStatus()
            } else {
                await this.updateEsdcUrl()
            }

            this.busy = false
        },

        esdcUrl(val) {
            this.enableEsdcUrlUpdate = val !== FiscalService.esdcUrl
        }
    },

    methods: {
        createCardForm() {
            return new Form(this, 'cardForm', {
                pin: null,
            })
        },

        createCertForm() {
            return new Form(this, 'certForm', {
                password: null,
                pac: null
            })
        },

        async chooseCertificate() {
            if (!this.certForm.validate()) return;

            this.busy = true
            const chooseCertificateResult = await FiscalService.chooseCertificate(this.certForm.password, this.certForm.pac)
            this.busy = false

            if(!chooseCertificateResult.success) {
                this.certError = this.$t('sdc.security_element_file_error')
                return
            }

            this.isCertificateSetup = true
        },

        async checkEsdcStatus() {
            this.busy = true
            await this.checkSdcStatus()
            this.busy = false
        },

        async verifyPin() {
            if (!this.cardForm.validate()) return;

            this.busy = true

            const verifyPinResult = await FiscalService.verifyPin(this.cardForm.pin, this.useDevEsdc ? 'DEVSDC' : 'ESDC')
            if(!verifyPinResult.success) {
                setTimeout(() => {
                    this.cardForm.$errors.pin = this.$t('validation.general')
                }, 200)
                this.pinFailed = true
                this.busy = false
                return
            }

            this.pinFailed = false
            await this.checkSdcStatus()

            this.busy = false
        },

        async updateEsdcUrl() {
            if(!this.esdcUrl || this.esdcUrl.length <= 7) return

            this.busy = true
            await FiscalService.setEsdcUrl(this.esdcUrl)
            await this.checkSdcStatus()
            this.busy = false
        },

        async checkSdcStatus() {
            const sdc = this.useDevEsdc ? 'DEVSDC' : 'ESDC'
            this.sdcStatus = await FiscalService.checkSdcStatus(sdc)
            this.enableEsdcUrlUpdate = this.esdcUrl !== FiscalService.esdcUrl
            this.uiData = (await FiscalService.getUiData('ESDC')).data || null
        },

        deleteCertificate() {
            this.$confirm(
                this.$t('general.are_you_sure'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('sdc.security_element_file_delete')
                },
                result => {
                    if (!result) return;

                    AppStorage.native().deleteItem('pac');
                    AppStorage.native().deleteItem('cert');
                    AppStorage.native().deleteItem('key');
                    AppStorage.native().deleteItem('certAlias');

                    FiscalService.isCertificateSetup = this.isCertificateSetup = false;
                }
            );
        }
    }
};
</script>

<style lang="scss" scoped>
.header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.drawer-wrapper {
    height: 100%;
    width: 100%;

    .drawer-content {
        max-height: 100%;
        overflow-y: auto;
        padding: 64px 20px 16px 20px;
        position: relative;
        height: 100%;
    }
}

.sdc-mgmt-content {
    min-height: 470px;

    .sdc-tabs {
        ::v-deep .v-slide-group__prev {
            display: none !important;
        }
    }

    .sdc-tab-content {
        padding: 20px;

        .sdc-info-container {
            display: flex;
            flex-wrap: wrap;
            margin: -8px -12px;

            .sdc-info {
                background-color: #f8f8f8;
                border: 1px solid lightgrey;
                border-radius: 4px;
                padding: 12px;
                margin-bottom: 16px;
                flex: 1;
                min-width: 300px;
                margin: 8px;

                .uppercase {
                    text-transform: uppercase;
                }
            }
        }
    }
}
</style>
